import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ResizeObserver from 'rc-resize-observer';
import { VariableSizeList as List } from 'react-window';
import { Row } from 'antd';
import { DateTime } from 'luxon';

import Permissions from '../../auth/Permissions';
import ResourceRow from './ResourceRow';
import ResourceDayModal from './ResourceDayModal';
import { getDaysInRange, getResourceRowLayout } from './resourceRowLayout';
import { resourceAxisPropType, resourceRowPropType } from './resourceAxis.propTypes';
import { getDayKey } from '../scheduleHelpers';
import { setBiweeklyModalConfig } from '../state/schedule.actions';

const AxisWidthContext = createContext(0);

const AxisInner = forwardRef(({ style, ...rest }, ref) => {
  const width = useContext(AxisWidthContext);
  return (
    <div ref={ref} style={{ ...style, width }} {...rest} />
  );
});
AxisInner.displayName = 'AxisInner';
AxisInner.propTypes = {
  style: PropTypes.shape({}),
};
AxisInner.defaultProps = {
  style: undefined,
};

export default function ResourceWeeklySchedule({
  axis,
  rows,
  searchText,
  shifts,
  onShiftSelect,
  onShiftEdit,
  editShift,
  onShiftCreate,
  newShift,
  onNewShiftChange,
  startDate,
  endDate,
  onDaySelect,
  viewType,
  onShiftDrop,
}) {
  const dispatch = useDispatch();

  const containerRef = useRef(null);
  const listRef = useRef();
  const listApiRef = useRef();
  const headerRef = useRef();
  const listContainerRef = useRef();
  const [tableDimensions, setTableDimensions] = useState();
  const { shiftField } = axis;

  const handleScroll = () => {
    if (listRef.current) {
      listRef.current.scrollLeft = headerRef?.current?.scrollLeft;
    }
  };

  const listScroll = (e) => {
    if (headerRef.current && e.target.className === 'user-biweekly-list') {
      headerRef.current.scrollLeft = listRef?.current?.scrollLeft;
    }
  };

  useLayoutEffect(() => {
    /*
      https://github.com/bvaughn/react-window/issues/256#issuecomment-735855406
    */
    if (listRef) {
      document.addEventListener('scroll', listScroll, true);
      return () => document.removeEventListener('scroll', listScroll, true);
    }
    return () => {};
  }, [listRef]);

  const displayedRows = useMemo(() => {
    if (!searchText) {
      return [undefined, ...rows];
    }
    const dayCount = Math.max(0, Math.round(endDate.diff(startDate, 'days').as('days')));
    const rowIdsWithShifts = new Set();
    let hasUnassignedShifts = false;
    for (let idx = 0; idx < dayCount; idx += 1) {
      const dayShifts = shifts[getDayKey(startDate.plus({ day: idx }))] || [];
      dayShifts.forEach((shift) => {
        const shiftRowIds = shift[shiftField] || [];
        if (shiftRowIds.length === 0) hasUnassignedShifts = true;
        shiftRowIds.forEach((rowId) => rowIdsWithShifts.add(rowId));
      });
    }
    const relevantRows = rows.filter(({ id }) => rowIdsWithShifts.has(id));
    return hasUnassignedShifts ? [undefined, ...relevantRows] : relevantRows;
  }, [rows, shiftField, searchText, shifts, startDate, endDate]);

  const handleShiftSelect = useCallback((shift) => {
    onShiftSelect(shift, Permissions.has('SCHEDULE_WRITE'));
    onShiftEdit(shift);
  }, [onShiftSelect, onShiftEdit]);

  const handleContainerRef = useCallback((element) => {
    if (element !== null) {
      const bounds = element.getBoundingClientRect();
      setTableDimensions(bounds);
      containerRef.current = element;
    }
  }, []);

  const onCloseDayModal = useCallback(() => {
    dispatch(setBiweeklyModalConfig({}));
  }, []);

  const numDays = Math.round(endDate.diff(startDate, 'days').as('days'));
  const rowLayouts = useMemo(() => displayedRows.map((row) => getResourceRowLayout({
    row,
    shifts,
    startDate,
    numDays,
    shiftField,
  })), [displayedRows, shifts, startDate, numDays, shiftField]);
  const days = getDaysInRange(startDate, numDays);
  const contentWidth = tableDimensions
    ? Math.max(tableDimensions.width, 160 + (days.length * 128))
    : 0;

  /*
    If the list has a vertical scrollbar we
    need to reduce the width of the header to match
  */
  const widthOffset = (
    (listContainerRef?.current?.clientWidth ?? 0)
    - (listRef?.current?.clientWidth ?? 0)
  );

  useLayoutEffect(() => {
    if (listApiRef.current) listApiRef.current.resetAfterIndex(0);
  }, [displayedRows, shifts, startDate, numDays]);

  return (
      <ResizeObserver
        onResize={({ height, width }) => {
          setTableDimensions({ height, width });
        }}
      >
        <div
          ref={handleContainerRef}
          className="schedule-user-weekly-view-container"
        >
          {
            tableDimensions
            && (
              <>
                <div
                  className="schedule-user-axis-header-scroll"
                  style={{ width: tableDimensions.width }}
                  ref={headerRef}
                  onScroll={handleScroll}
                >
                <Row
                  className="schedule-user-weekly-header-container"
                  style={{
                    width: contentWidth,
                    minWidth: '100%',
                    overflow: 'visible',
                    paddingRight: widthOffset,
                  }}
                >
                  <div className="schedule-user-axis-users-header">
                    {axis.headerTitle}
                  </div>
                  {
                    days.map((dt, index) => {
                      const prefix = dt.day === 1 && index > 0
                        ? `${dt.toFormat('LLL')} `
                        : '';
                      const singleDay = days.length === 1;
                      return (
                        <div
                          className="schedule-user-axis-day-header"
                          key={dt.toSQLDate()}
                        >
                          <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                            className={singleDay ? 'schedule-user-axis-day-title' : 'schedule-user-axis-day-label'}
                            onClick={() => onDaySelect(dt)}
                            tabIndex={-1}
                            onKeyDown={() => {}}
                          >
                            {singleDay
                              ? dt.toFormat('cccc, LLL d')
                              : (
                                <>
                                  <span>{`${prefix}${dt.day}`}</span>
                                  <span>{dt.toFormat('ccc').toUpperCase()}</span>
                                </>
                              )}
                          </div>
                        </div>
                      );
                    })
                }
                </Row>
                </div>
                <Row
                  style={{ width: tableDimensions.width, overflow: 'hidden' }}
                  ref={listContainerRef}
                >
                  <AxisWidthContext.Provider value={contentWidth}>
                    <List
                      ref={listApiRef}
                      height={tableDimensions.height - 48} // Header height
                      itemCount={displayedRows.length}
                      itemSize={(index) => rowLayouts[index].rowHeight}
                      width={tableDimensions.width}
                      innerElementType={AxisInner}
                      outerRef={listRef}
                      className="user-biweekly-list"
                    >
                    {({ index, style }) => {
                      const currentRow = displayedRows[index];
                      return (
                        <ResourceRow
                          axis={axis}
                          row={currentRow}
                          key={currentRow?.id || 'unassigned'}
                          startDate={startDate}
                          numDays={numDays}
                          shifts={shifts}
                          onShiftSelect={handleShiftSelect}
                          onShiftDrop={onShiftDrop}
                          rowLayout={rowLayouts[index]}
                          style={{ ...style, width: contentWidth }}
                        />
                      );
                    }}
                    </List>
                  </AxisWidthContext.Provider>
                </Row>
              </>
            )
          }
          {tableDimensions && (
            <ResourceDayModal
              axis={axis}
              rows={rows}
              onCloseClicked={onCloseDayModal}
              onShiftSelect={handleShiftSelect}
              onShiftEdit={onShiftEdit}
              editShift={editShift}
              onShiftCreate={onShiftCreate}
              newShift={newShift}
              onNewShiftChange={onNewShiftChange}
              viewType={viewType}
            />
          )}
        </div>
      </ResizeObserver>
  );
}

/* eslint-disable react/forbid-prop-types */
ResourceWeeklySchedule.propTypes = {
  axis: resourceAxisPropType.isRequired,
  rows: PropTypes.arrayOf(resourceRowPropType).isRequired,
  searchText: PropTypes.string,
  shifts: PropTypes.object.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  onShiftEdit: PropTypes.func.isRequired,
  editShift: PropTypes.object,
  onShiftCreate: PropTypes.func.isRequired,
  newShift: PropTypes.object,
  onNewShiftChange: PropTypes.func.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  onDaySelect: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  onShiftDrop: PropTypes.func.isRequired,
};

ResourceWeeklySchedule.defaultProps = {
  searchText: undefined,
  editShift: undefined,
  newShift: undefined,
  startDate: DateTime.local(),
  endDate: DateTime.local(),
};
